/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  getDetailedDriverOptions,
  getDetailedIronOptions,
  getDetailedWedgeOptions,
  submitDetailedDriverRatings,
  submitDetailedIronRatings,
  submitDetailedWedgeRatings,
  getAllDriverHeads,
  getAllIronHeads,
  getAllWedgeModels,
  getAllShafts,
  DetailedDriverOptions,
  DetailedIronOptions,
  DetailedWedgeOptions,
  DetailedDriverRating,
  DetailedIronRating,
  DetailedWedgeRating,
  DriverHeadOption,
  IronHeadOption,
  WedgeModelOption,
  ShaftOption
} from '../services/expertDetailedRatingService';
import GolfLoadingSpinner from './GolfLoadingSpinner';
import SkeletonLoader from './SkeletonLoader';
import CustomComboAdder from './CustomComboAdder';
import { useRatingDraft, DraftPayload } from '../hooks/useRatingDraft';

interface ProfileData {
  id: number;
  [key: string]: any;
}

interface DetailedRatingModalProps {
  isOpen: boolean;
  profile: ProfileData;
  selectedProfileFields: string[];
  onClose: () => void;
  onSuccess: () => void;
}

type EquipmentCategory = 'driver' | 'iron' | 'wedge';

// GROUP 1: Head + Configuration ratings
interface HeadConfigRating {
  headId: number;
  config: string | number; // loft for drivers, set for irons, loft config for wedges
  rating?: number;
}

// GROUP 2: Shaft + Flex ratings
interface ShaftFlexRating {
  shaftId: number;
  flex: string;
  rating?: number;
}

// GROUP 3: Final combination ratings
interface CombinationRating {
  headId: number;
  config: string | number;
  shaftId: number;
  flex: string;
  combinedRating?: number;
  notes?: string;
}

// FastAPI validation errors (422) return `detail` as an array of {type, loc, msg, input}
// objects rather than a string -- render that directly as JSX and React throws
// "Objects are not valid as a React child" instead of showing the actual error.
const extractErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg ? `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}` : JSON.stringify(e)).join('; ');
  }
  return fallback;
};

// GROUP 4: Custom combo interfaces (NEW for Step 2.5)
interface CustomHeadCombo {
  id: string; // UUID
  headId: number;
  headBrand: string;
  headModel: string;
  config: string | number; // loft or set config
  rating: 9 | 10;
  isCustom: true;
}

interface CustomShaftCombo {
  id: string; // UUID
  shaftId: number;
  shaftBrand: string;
  shaftModel: string;
  flex: string | number;
  rating: 9 | 10;
  isCustom: true;
}

const DetailedRatingModal_v2: React.FC<DetailedRatingModalProps> = ({
  isOpen,
  profile,
  selectedProfileFields,
  onClose,
  onSuccess
}) => {
  const [category, setCategory] = useState<EquipmentCategory>('driver');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 2.5 | 3>(1);
  const [profileContextMinimized, setProfileContextMinimized] = useState(false);

  // Equipment options
  const [driverOptions, setDriverOptions] = useState<DetailedDriverOptions | null>(null);
  const [ironOptions, setIronOptions] = useState<DetailedIronOptions | null>(null);
  const [wedgeOptions, setWedgeOptions] = useState<DetailedWedgeOptions | null>(null);

  // Full equipment lists (for custom combo dropdown showing ALL options from DB)
  const [allDriverHeads, setAllDriverHeads] = useState<any[]>([]);
  const [allIronHeads, setAllIronHeads] = useState<any[]>([]);
  const [allWedgeModels, setAllWedgeModels] = useState<any[]>([]);
  const [allShafts, setAllShafts] = useState<any[]>([]);

  // Grouped ratings storage
  const [headConfigRatings, setHeadConfigRatings] = useState<Map<string, number>>(new Map());
  const [shaftFlexRatings, setShaftFlexRatings] = useState<Map<string, number>>(new Map());
  const [combinationRatings, setCombinationRatings] = useState<Map<string, { rating: number; notes?: string }>>(new Map());
  const [hasLoadedSavedRatings, setHasLoadedSavedRatings] = useState(false);

  // Custom combo state (NEW for Step 2.5)
  const [customHeadCombos, setCustomHeadCombos] = useState<CustomHeadCombo[]>([]);
  const [customShaftCombos, setCustomShaftCombos] = useState<CustomShaftCombo[]>([]);
  const [selectedCombinations, setSelectedCombinations] = useState<any[]>([]);

  // Constants for custom combos
  const MAX_CUSTOM_HEADS = 3;
  const MAX_CUSTOM_SHAFTS = 3;
  const MAX_STEP3_INITIAL_COMBOS = 40;
  const MAX_STEP3_TOTAL_COMBOS = 60;

  // ============================================
  // DRAFT PERSISTENCE (server-side, M3)
  // ============================================
  //
  // Drafts live on the server, so a fitter can close the laptop mid-pass and resume on
  // another machine. `useRatingDraft` owns the transport and the offline cache; this
  // component owns only the payload shape — the six pieces of state below, with the
  // three Maps flattened to entry arrays for JSON.

  const {
    draft: savedDraft,
    loaded: draftLoaded,
    status: draftStatus,
    saveDraft,
    flushDraft,
    clearDraft
  } = useRatingDraft(profile.id, category, isOpen);

  const serializeDraft = (): DraftPayload => ({
    head: Array.from(headConfigRatings.entries()),
    shaft: Array.from(shaftFlexRatings.entries()),
    combo: Array.from(combinationRatings.entries()),
    custom_heads: customHeadCombos,
    custom_shafts: customShaftCombos,
    selected: selectedCombinations
  });

  // Set once a category has been submitted, to stop the auto-save effect writing a
  // fresh draft from the state that is still on screen while the modal tears down.
  const submittedRef = useRef(false);

  const draftIsEmpty = (payload: DraftPayload): boolean =>
    !payload.head?.length && !payload.shaft?.length && !payload.combo?.length &&
    !payload.custom_heads?.length && !payload.custom_shafts?.length &&
    !payload.selected?.length;

  // Restore once the server copy has arrived. Runs on open and on category switch;
  // an absent draft resets to a clean step 1 rather than leaving the previous
  // category's ratings on screen.
  useEffect(() => {
    if (!isOpen || !draftLoaded) return;
    submittedRef.current = false;

    if (savedDraft && !draftIsEmpty(savedDraft)) {
      setHeadConfigRatings(new Map(savedDraft.head || []));
      setShaftFlexRatings(new Map(savedDraft.shaft || []));
      setCombinationRatings(new Map(savedDraft.combo || []));
      setCustomHeadCombos(savedDraft.custom_heads || []);
      setCustomShaftCombos(savedDraft.custom_shafts || []);
      setSelectedCombinations(savedDraft.selected || []);
      setHasLoadedSavedRatings(true);
    } else {
      setCurrentStep(1);
      setHeadConfigRatings(new Map());
      setShaftFlexRatings(new Map());
      setCombinationRatings(new Map());
      setCustomHeadCombos([]);
      setCustomShaftCombos([]);
      setSelectedCombinations([]);
      setHasLoadedSavedRatings(false);
    }
  }, [isOpen, draftLoaded, savedDraft]);

  // Auto-save. Unlike the six localStorage effects this replaces, it is NOT gated on
  // the state being non-empty: that gate meant deleting every rating never persisted,
  // so a reload resurrected work the fitter had deliberately cleared.
  useEffect(() => {
    if (!isOpen || !draftLoaded || submittedRef.current) return;
    saveDraft(serializeDraft());
  }, [
    headConfigRatings, shaftFlexRatings, combinationRatings,
    customHeadCombos, customShaftCombos, selectedCombinations,
    isOpen, draftLoaded
  ]);

  // Load equipment options when the modal opens. Restoring saved work is the draft
  // effect's job above — it can't run here because the server round-trip is async.
  useEffect(() => {
    if (isOpen) {
      loadEquipmentOptions();
    }
  }, [isOpen, profile.id, category]);

  // Warn user before closing browser/tab with unsaved ratings
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedWork = headConfigRatings.size > 0 || shaftFlexRatings.size > 0 || combinationRatings.size > 0;
      if (isOpen && hasUnsavedWork) {
        e.preventDefault();
        e.returnValue = 'You have unsaved ratings. Your progress is saved to your account and will be restored when you return.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOpen, headConfigRatings, shaftFlexRatings, combinationRatings]);

  const loadEquipmentOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [drivers, irons, wedges, allDriverHeadsData, allIronHeadsData, allWedgeModelsData, allShaftsData] = await Promise.all([
        getDetailedDriverOptions(profile.id),
        getDetailedIronOptions(profile.id),
        getDetailedWedgeOptions(profile.id),
        getAllDriverHeads(),
        getAllIronHeads(),
        getAllWedgeModels(),
        getAllShafts(category)
      ]);
      setDriverOptions(drivers);
      setIronOptions(irons);
      setWedgeOptions(wedges);
      setAllDriverHeads(allDriverHeadsData);
      setAllIronHeads(allIronHeadsData);
      setAllWedgeModels(allWedgeModelsData);
      setAllShafts(allShaftsData);
    } catch (err: any) {
      console.error('Error loading equipment options:', err);
      setError(extractErrorMessage(err, 'Failed to load equipment options'));
    } finally {
      setLoading(false);
    }
  };

  const getHeadConfigKey = (headId: number, config: string | number): string => {
    return `head-${headId}-config-${config}`;
  };

  const getShaftFlexKey = (shaftId: number, flex: string | number): string => {
    return `shaft-${shaftId}-flex-${flex}`;
  };

  const getCombinationKey = (headId: number, config: string | number, shaftId: number, flex: string | number): string => {
    return `${headId}-${config}-${shaftId}-${flex}`;
  };

  // headId and shaftId are always numeric (no hyphens) and flex is a bare code
  // ("R"/"S"/"X", no hyphens), so headId is always the first '-'-delimited part and
  // flex/shaftId are always the last two -- whatever's left in the middle is config,
  // even when it itself contains hyphens (iron set configs like "4-PW", wedge loft
  // configs like "56-60-64"). Parsing from both ends instead of taking the first 4
  // positional parts recovers config correctly without needing a different delimiter
  // or breaking keys already persisted in drafts under the old format.
  const parseCombinationKey = (key: string): { headId: string; config: string; shaftId: string; flex: string } => {
    const parts = key.split('-');
    return {
      headId: parts[0],
      flex: parts[parts.length - 1],
      shaftId: parts[parts.length - 2],
      config: parts.slice(1, parts.length - 2).join('-')
    };
  };

  const updateHeadConfigRating = (headId: number, config: string | number, rating: number) => {
    const key = getHeadConfigKey(headId, config);
    setHeadConfigRatings(new Map(headConfigRatings.set(key, rating)));
  };

  const updateShaftFlexRating = (shaftId: number, flex: string, rating: number) => {
    const key = getShaftFlexKey(shaftId, flex);
    setShaftFlexRatings(new Map(shaftFlexRatings.set(key, rating)));
  };

  const updateCombinationRating = (
    headId: number,
    config: string | number,
    shaftId: number,
    flex: string,
    rating: number,
    notes?: string
  ) => {
    const key = getCombinationKey(headId, config, shaftId, flex);
    setCombinationRatings(new Map(combinationRatings.set(key, { rating, notes })));
  };

  const updateCombinationNotes = (
    headId: number,
    config: string | number,
    shaftId: number,
    flex: string,
    notes: string
  ) => {
    const key = getCombinationKey(headId, config, shaftId, flex);
    const existing = combinationRatings.get(key) || { rating: 0 };
    setCombinationRatings(new Map(combinationRatings.set(key, { ...existing, notes })));
  };

  // ============================================
  // CUSTOM COMBO MANAGEMENT FUNCTIONS (NEW for Step 2.5)
  // ============================================

  const addCustomHeadCombo = (
    headId: number,
    headBrand: string,
    headModel: string,
    config: string | number,
    rating: 9 | 10
  ) => {
    if (customHeadCombos.length >= MAX_CUSTOM_HEADS) {
      toast.error(`Maximum ${MAX_CUSTOM_HEADS} custom heads allowed`);
      return;
    }

    const newCombo: CustomHeadCombo = {
      id: crypto.randomUUID(),
      headId,
      headBrand,
      headModel,
      config,
      rating,
      isCustom: true
    };

    setCustomHeadCombos([...customHeadCombos, newCombo]);

    // Also add to headConfigRatings
    const key = getHeadConfigKey(headId, config);
    setHeadConfigRatings(new Map(headConfigRatings.set(key, rating)));

    toast.success(`Added custom combo: ${headBrand} ${headModel}`);
  };

  const removeCustomHeadCombo = (id: string) => {
    setCustomHeadCombos(customHeadCombos.filter(c => c.id !== id));
  };

  const addCustomShaftCombo = (
    shaftId: number,
    shaftBrand: string,
    shaftModel: string,
    flex: string | number,
    rating: 9 | 10
  ) => {
    if (customShaftCombos.length >= MAX_CUSTOM_SHAFTS) {
      toast.error(`Maximum ${MAX_CUSTOM_SHAFTS} custom shafts allowed`);
      return;
    }

    const newCombo: CustomShaftCombo = {
      id: crypto.randomUUID(),
      shaftId,
      shaftBrand,
      shaftModel,
      flex,
      rating,
      isCustom: true
    };

    setCustomShaftCombos([...customShaftCombos, newCombo]);

    // Also add to shaftFlexRatings
    const key = getShaftFlexKey(shaftId, String(flex));
    setShaftFlexRatings(new Map(shaftFlexRatings.set(key, rating)));

    toast.success(`Added custom combo: ${shaftBrand} ${shaftModel} (${flex})`);
  };

  const removeCustomShaftCombo = (id: string) => {
    setCustomShaftCombos(customShaftCombos.filter(c => c.id !== id));
  };

  // ============================================
  // AUTO-SELECTION ALGORITHM (NEW for Step 2.5)
  // ============================================

  // Helper function: Cross product of two arrays
  const crossProduct = (arr1: any[], arr2: any[]) => {
    const result: any[] = [];
    arr1.forEach(item1 => {
      arr2.forEach(item2 => {
        result.push({ ...item1, ...item2 });
      });
    });
    return result;
  };

  // Helper function: Random sample from array
  const randomSample = (arr: any[], count: number) => {
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, arr.length));
  };

  // Calculate auto-selected combinations using head-based tiered algorithm
  const calculateAutoSelectedCombos = () => {
    // PHASE 1: Group all rated head+configs by HEAD ID (not head+config)
    const headGroups = new Map<number, any[]>();
    headConfigRatings.forEach((rating, key) => {
      const parsed = parseHeadConfigKey(key);
      if (parsed) {
        if (!headGroups.has(parsed.headId)) {
          headGroups.set(parsed.headId, []);
        }
        headGroups.get(parsed.headId)!.push({ ...parsed, rating });
      }
    });

    // PHASE 2: For each HEAD ID, select the highest-rated config
    const bestHeadConfigs: any[] = [];
    headGroups.forEach((configs, headId) => {
      // Sort configs by rating and take the best one
      configs.sort((a, b) => b.rating - a.rating);
      bestHeadConfigs.push(configs[0]);
    });

    // PHASE 3: Sort HEAD MODELS by their best config rating and select top 4
    bestHeadConfigs.sort((a, b) => b.rating - a.rating);
    const topHeads = bestHeadConfigs.slice(0, 4);
    const tierComboCounts = [12, 10, 10, 8]; // Combo count per head

    // Helper: Get shaft model key (brand + model, without flex/weight)
    const getShaftModelKey = (shaftId: number, flex: string): string | null => {
      const shaftName = getShaftName(shaftId, flex);
      if (shaftName === 'Unknown') return null;
      // Extract brand + model (remove flex in parentheses and weight suffix)
      return shaftName.replace(/\s*\([^)]*\)\s*/g, '').replace(/\s+[\d.]+g$/g, '');
    };

    // Helper: Group shaft ratings by model (aggregate all flex variants)
    const getShaftModelRatings = (): Map<string, { shaftIds: Set<number>, ratings: number[], avgRating: number }> => {
      const modelGroups = new Map<string, { shaftIds: Set<number>, ratings: number[] }>();

      shaftFlexRatings.forEach((rating, key) => {
        const parsed = parseShaftFlexKey(key);
        if (!parsed) return;

        const modelKey = getShaftModelKey(parsed.shaftId, parsed.flex);
        if (!modelKey) return;

        if (!modelGroups.has(modelKey)) {
          modelGroups.set(modelKey, { shaftIds: new Set(), ratings: [] });
        }

        const group = modelGroups.get(modelKey)!;
        group.shaftIds.add(parsed.shaftId);
        group.ratings.push(rating);
      });

      // Calculate average ratings
      const result = new Map<string, { shaftIds: Set<number>, ratings: number[], avgRating: number }>();
      modelGroups.forEach((group, modelKey) => {
        const avgRating = group.ratings.reduce((sum, r) => sum + r, 0) / group.ratings.length;
        result.set(modelKey, { ...group, avgRating });
      });

      return result;
    };

    const shaftModelRatings = getShaftModelRatings();

    // PHASE 4: For each head MODEL, generate combinations using tiered shaft selection
    const allCombos: any[] = [];
    const highHeads: any[] = [];
    const highShafts: any[] = [];

    topHeads.forEach((head, headIndex) => {
      const maxCombos = tierComboCounts[headIndex] || 8;
      const headCombos: any[] = [];

      // Sort shaft models by average rating
      const sortedModels = Array.from(shaftModelRatings.entries())
        .sort((a, b) => b[1].avgRating - a[1].avgRating);

      // Select top 4 shaft models
      const topModels = sortedModels.slice(0, 4);

      // For each top shaft model, add variants based on recommendation count
      topModels.forEach(([modelKey, modelData]) => {
        const modelShaftIds = Array.from(modelData.shaftIds);

        // Get all variants for this model with their ratings
        const variants: any[] = [];
        shaftFlexRatings.forEach((rating, key) => {
          const parsed = parseShaftFlexKey(key);
          if (parsed && modelShaftIds.includes(parsed.shaftId)) {
            variants.push({ ...parsed, rating });
          }
        });

        // Count recommended variants (rating > 7)
        const recommendedVariants = variants.filter(v => v.rating > 7);
        const otherVariants = variants.filter(v => v.rating <= 7);

        // Apply variant strategy
        if (recommendedVariants.length === 1) {
          // 1 recommended: take 1 recommended + 1 random other = 2 total
          headCombos.push(...recommendedVariants.slice(0, 1));
          headCombos.push(...randomSample(otherVariants, 1));
        } else if (recommendedVariants.length >= 2) {
          // 2+ recommended: take 2 recommended + 2 random others = 4 total
          headCombos.push(...recommendedVariants.slice(0, 2));
          headCombos.push(...randomSample(otherVariants, 2));
        } else {
          // No recommended: take 2 random variants
          headCombos.push(...randomSample(variants, 2));
        }
      });

      // Add random shaft models for exploration (4 random models, 1 variant each)
      const usedModelKeys = new Set(topModels.map(([key]) => key));
      const remainingModels = sortedModels.filter(([key]) => !usedModelKeys.has(key));
      const randomModels = randomSample(remainingModels, 4);

      randomModels.forEach(([modelKey, modelData]) => {
        const modelShaftIds = Array.from(modelData.shaftIds);
        const variants: any[] = [];
        shaftFlexRatings.forEach((rating, key) => {
          const parsed = parseShaftFlexKey(key);
          if (parsed && modelShaftIds.includes(parsed.shaftId)) {
            variants.push({ ...parsed, rating });
          }
        });
        headCombos.push(...randomSample(variants, 1));
      });

      // Build final combinations for this head (cap at maxCombos)
      const finalHeadCombos = headCombos.slice(0, maxCombos).map(shaft => ({
        headId: head.headId,
        config: head.config,
        shaftId: shaft.shaftId,
        flex: shaft.flex,
        headRating: head.rating,
        shaftRating: shaft.rating
      }));

      allCombos.push(...finalHeadCombos);

      // Track high heads/shafts for expert-curated combos
      if (head.rating > 7) highHeads.push(head);
      finalHeadCombos.forEach(combo => {
        if (combo.shaftRating > 7) {
          highShafts.push({ shaftId: combo.shaftId, flex: combo.flex, rating: combo.shaftRating });
        }
      });
    });

    // Add headName and shaftName to each combo
    const finalCombos = allCombos.map(combo => ({
      ...combo,
      headName: getHeadName(combo.headId, combo.config),
      shaftName: getShaftName(combo.shaftId, combo.flex)
    }));

    return {
      combos: finalCombos,
      highHeads,
      highShafts,
      highHigh: finalCombos.filter(c => c.headRating > 7 && c.shaftRating > 7).length,
      highLow: finalCombos.filter(c => c.headRating > 7 && c.shaftRating <= 4).length,
      lowLow: finalCombos.filter(c => c.headRating <= 4 && c.shaftRating <= 4).length,
      random: finalCombos.filter(c => c.headRating > 4 && c.headRating <= 7).length
    };
  };

  // Calculate expert-curated combinations
  const calculateExpertCuratedCombos = (autoHighHeads: any[], autoHighShafts: any[]) => {
    const combos: any[] = [];

    // Type 1: Custom × Custom (⭐⭐)
    customHeadCombos.forEach(head => {
      customShaftCombos.forEach(shaft => {
        combos.push({
          headId: head.headId,
          config: head.config,
          shaftId: shaft.shaftId,
          flex: shaft.flex,
          headName: `${head.headBrand} ${head.headModel}`,
          shaftName: `${shaft.shaftBrand} ${shaft.shaftModel}`,
          badge: '⭐⭐',
          expertCurated: true,
          headRating: head.rating,
          shaftRating: shaft.rating
        });
      });
    });

    // Type 2: Custom head × Auto shaft (⭐)
    customHeadCombos.forEach(head => {
      autoHighShafts.forEach(shaft => {
        combos.push({
          headId: head.headId,
          config: head.config,
          shaftId: shaft.shaftId,
          flex: shaft.flex,
          headName: `${head.headBrand} ${head.headModel}`,
          shaftName: getShaftName(shaft.shaftId, shaft.flex),
          badge: '⭐',
          expertCurated: true
        });
      });
    });

    // Type 3: Auto head × Custom shaft (⭐)
    autoHighHeads.forEach(head => {
      customShaftCombos.forEach(shaft => {
        combos.push({
          headId: head.headId,
          config: head.config,
          shaftId: shaft.shaftId,
          flex: shaft.flex,
          headName: getHeadName(head.headId, head.config),
          shaftName: `${shaft.shaftBrand} ${shaft.shaftModel}`,
          badge: '⭐',
          expertCurated: true
        });
      });
    });

    return combos;
  };

  // Helper: Parse head config key
  const parseHeadConfigKey = (key: string) => {
    const match = key.match(/head-(\d+)-config-(.+)/);
    if (!match) return null;
    return {
      headId: parseInt(match[1]),
      config: isNaN(Number(match[2])) ? match[2] : Number(match[2])
    };
  };

  // Helper: Parse shaft flex key
  const parseShaftFlexKey = (key: string) => {
    const match = key.match(/shaft-(\d+)-flex-(.+)/);
    if (!match) return null;
    return {
      shaftId: parseInt(match[1]),
      flex: match[2]
    };
  };

  // Helper: Get head name
  const getHeadName = (headId: number, config: string | number): string => {
    if (category === 'driver' && driverOptions) {
      const head = driverOptions.driver_heads.find(h => h.head_id === headId);
      if (head) return `${head.head_brand} ${head.head_model} @ ${config}°`;
    }
    if (category === 'iron' && ironOptions) {
      const head = ironOptions.iron_heads.find(h => h.head_id === headId);
      if (head) return `${head.head_brand} ${head.head_model} - ${config}`;
    }
    if (category === 'wedge' && wedgeOptions) {
      const model = wedgeOptions.wedge_models.find(m => m.model_id === headId);
      if (model) return `${model.model_brand} ${model.model_name} - ${config}`;
    }
    return 'Unknown';
  };

  // Helper: Get shaft name
  const getShaftName = (shaftId: number, flex: string): string => {
    // NEW structure: search through shaft_models.flex_variants to find matching shaft_id
    if (category === 'driver' && driverOptions) {
      for (const head of driverOptions.driver_heads) {
        for (const shaftModel of head.shaft_models || []) {
          const variant = shaftModel.flex_variants?.find(v => v.shaft_id === shaftId);
          if (variant) {
            return `${shaftModel.shaft_brand} ${shaftModel.shaft_model} (${flex})`;
          }
        }
      }
    } else if (category === 'iron' && ironOptions) {
      for (const head of ironOptions.iron_heads) {
        for (const shaftModel of head.shaft_models || []) {
          const variant = shaftModel.flex_variants?.find(v => v.shaft_id === shaftId);
          if (variant) {
            return `${shaftModel.shaft_brand} ${shaftModel.shaft_model} (${flex})`;
          }
        }
      }
    } else if (category === 'wedge' && wedgeOptions) {
      for (const model of wedgeOptions.wedge_models) {
        for (const shaftModel of model.shaft_models || []) {
          const variant = shaftModel.flex_variants?.find(v => v.shaft_id === shaftId);
          if (variant) {
            return `${shaftModel.shaft_brand} ${shaftModel.shaft_model} (${flex})`;
          }
        }
      }
    }

    return 'Unknown';
  };

  // Generate all possible combinations (for random filling)
  const generateAllPossibleCombinations = () => {
    const combinations: any[] = [];

    if (category === 'driver' && driverOptions) {
      driverOptions.driver_heads.forEach(head => {
        // Loft options are now at HEAD level
        head.loft_options?.forEach(loft => {
          head.shaft_models?.forEach(shaftModel => {
            shaftModel.flex_variants?.forEach(flexVariant => {
              combinations.push({
                headId: head.head_id,
                config: loft,
                shaftId: flexVariant.shaft_id,
                flex: flexVariant.standard_flex,
                headName: `${head.head_brand} ${head.head_model} @ ${loft}°`,
                shaftName: `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`,
                isRecommended: flexVariant.is_recommended_flex
              });
            });
          });
        });
      });
    } else if (category === 'iron' && ironOptions) {
      ironOptions.iron_heads.forEach(head => {
        // Set config options are now at HEAD level
        head.set_config_options?.forEach(setConfig => {
          head.shaft_models?.forEach(shaftModel => {
            shaftModel.flex_variants?.forEach(flexVariant => {
              combinations.push({
                headId: head.head_id,
                config: setConfig,
                shaftId: flexVariant.shaft_id,
                flex: flexVariant.standard_flex,
                headName: `${head.head_brand} ${head.head_model} - ${setConfig}`,
                shaftName: `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`,
                isRecommended: flexVariant.is_recommended_flex
              });
            });
          });
        });
      });
    } else if (category === 'wedge' && wedgeOptions) {
      wedgeOptions.wedge_models.forEach(model => {
        // Loft config options are now at MODEL level
        model.loft_configs?.forEach(loftConfig => {
          model.shaft_models?.forEach(shaftModel => {
            shaftModel.flex_variants?.forEach(flexVariant => {
              combinations.push({
                headId: model.model_id,
                config: loftConfig,
                shaftId: flexVariant.shaft_id,
                flex: flexVariant.standard_flex,
                headName: `${model.model_brand} ${model.model_name} - ${loftConfig}`,
                shaftName: `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`,
                isRecommended: flexVariant.is_recommended_flex
              });
            });
          });
        });
      });
    }

    return combinations;
  };

  const handleClose = async () => {
    const hasUnsavedWork = headConfigRatings.size > 0 || shaftFlexRatings.size > 0 || combinationRatings.size > 0;

    if (hasUnsavedWork) {
      const confirmClose = window.confirm(
        'You have unsaved ratings. Your progress is saved to your account and will be restored when you return — on this machine or any other.\n\nAre you sure you want to close this modal?'
      );
      if (!confirmClose) return;
    }

    // Don't let a debounced save die with the modal.
    await flushDraft();
    onClose();
  };

  const handleClearStep = () => {
    const confirmClear = window.confirm(
      `Are you sure you want to clear all ratings for Step ${currentStep}? This cannot be undone.`
    );

    if (!confirmClear) return;

    // Clearing the state is enough — the auto-save effect persists the emptied step.
    if (currentStep === 1) {
      setHeadConfigRatings(new Map());
      toast.success('Step 1 ratings cleared');
    } else if (currentStep === 2) {
      setShaftFlexRatings(new Map());
      toast.success('Step 2 ratings cleared');
    } else if (currentStep === 3) {
      setCombinationRatings(new Map());
      toast.success('Step 3 ratings cleared');
    }
  };

  const handleSubmit = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    setSubmitting(true);
    setError(null);

    try {
      const driverRatings: DetailedDriverRating[] = [];
      const ironRatings: DetailedIronRating[] = [];
      const wedgeRatings: DetailedWedgeRating[] = [];

      // Convert grouped ratings to backend format
      combinationRatings.forEach((value, key) => {
        const { headId, config, shaftId, flex } = parseCombinationKey(key);
        const headConfigKey = getHeadConfigKey(parseInt(headId), category === 'driver' ? parseFloat(config) : config);
        const shaftFlexKey = getShaftFlexKey(parseInt(shaftId), flex);

        const headConfigRating = headConfigRatings.get(headConfigKey) || 5;
        const shaftFlexRating = shaftFlexRatings.get(shaftFlexKey) || 5;

        if (!value.rating) return; // Skip if no combined rating

        if (category === 'driver') {
          driverRatings.push({
            driver_head_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            loft_degrees: parseFloat(config),
            head_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            loft_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        } else if (category === 'iron') {
          ironRatings.push({
            iron_head_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            set_configuration: config,
            head_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            set_config_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        } else if (category === 'wedge') {
          wedgeRatings.push({
            wedge_model_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            loft_configuration: config.toString(),
            model_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            loft_set_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        }
      });

      // Submit based on category
      if (category === 'driver' && driverRatings.length > 0) {
        await submitDetailedDriverRatings({ profile_id: profile.id, ratings: driverRatings });
      } else if (category === 'iron' && ironRatings.length > 0) {
        await submitDetailedIronRatings({ profile_id: profile.id, ratings: ironRatings });
      } else if (category === 'wedge' && wedgeRatings.length > 0) {
        await submitDetailedWedgeRatings({ profile_id: profile.id, ratings: wedgeRatings });
      }

      // Only this category's draft goes. Clearing all three would destroy in-progress
      // work in the others -- which M2 now deliberately keeps the profile in the queue
      // for. (The submit endpoint drops the server row too; this clears the local copy.)
      submittedRef.current = true;
      await clearDraft();

      toast.success('Ratings submitted successfully!');
      onSuccess();
    } catch (err: any) {
      console.error('Error submitting ratings:', err);
      const errorMsg = extractErrorMessage(err, 'Failed to submit ratings');
      setError(errorMsg);

      // Retry logic for network errors
      if (retryCount < MAX_RETRIES && (err.code === 'ERR_NETWORK' || err.message?.includes('network'))) {
        const retryDelay = 2000 * (retryCount + 1); // Exponential backoff
        toast.error(`Network error. Retrying in ${retryDelay/1000}s... (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => handleSubmit(retryCount + 1), retryDelay);
      } else {
        toast.error(`Submission failed: ${errorMsg}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePartialSaveAndNext = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const driverRatings: DetailedDriverRating[] = [];
      const ironRatings: DetailedIronRating[] = [];
      const wedgeRatings: DetailedWedgeRating[] = [];

      // Convert any existing ratings to backend format (even if 0)
      combinationRatings.forEach((value, key) => {
        const { headId, config, shaftId, flex } = parseCombinationKey(key);
        const headConfigKey = getHeadConfigKey(parseInt(headId), category === 'driver' ? parseFloat(config) : config);
        const shaftFlexKey = getShaftFlexKey(parseInt(shaftId), flex);

        const headConfigRating = headConfigRatings.get(headConfigKey) || 5;
        const shaftFlexRating = shaftFlexRatings.get(shaftFlexKey) || 5;

        if (!value.rating) return; // Skip if no combined rating

        if (category === 'driver') {
          driverRatings.push({
            driver_head_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            loft_degrees: parseFloat(config),
            head_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            loft_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        } else if (category === 'iron') {
          ironRatings.push({
            iron_head_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            set_configuration: config,
            head_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            set_config_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        } else if (category === 'wedge') {
          wedgeRatings.push({
            wedge_model_id: parseInt(headId),
            shaft_id: parseInt(shaftId),
            shaft_flex: flex,
            loft_configuration: config.toString(),
            model_rating: headConfigRating,
            shaft_model_rating: shaftFlexRating,
            shaft_flex_rating: shaftFlexRating,
            loft_set_rating: headConfigRating,
            combined_rating: value.rating,
            notes: value.notes
          });
        }
      });

      // Submit partial ratings (can be empty)
      if (category === 'driver') {
        await submitDetailedDriverRatings({ profile_id: profile.id, ratings: driverRatings });
      } else if (category === 'iron') {
        await submitDetailedIronRatings({ profile_id: profile.id, ratings: ironRatings });
      } else if (category === 'wedge') {
        await submitDetailedWedgeRatings({ profile_id: profile.id, ratings: wedgeRatings });
      }

      submittedRef.current = true;
      await clearDraft();

      const ratingCount = driverRatings.length + ironRatings.length + wedgeRatings.length;
      toast.success(`Saved ${ratingCount} rating(s) and moving to next profile`);

      // Move to next profile
      onSuccess();
    } catch (err: any) {
      console.error('Error saving partial ratings:', err);
      const errorMsg = extractErrorMessage(err, 'Failed to save partial ratings');
      setError(errorMsg);
      toast.error(`Save failed: ${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to chunk array into groups of N
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  const renderRatingButtons = (
    currentRating: number | undefined,
    onRate: (rating: number) => void,
    label: string,
    size: 'small' | 'medium' = 'medium'
  ) => {
    const buttonClass = size === 'small' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm';

    return (
      <div className="mb-3">
        <label className="text-sm font-semibold text-gray-700 block mb-2">{label}</label>
        <div className="flex gap-1 flex-wrap">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
            <button
              key={rating}
              onClick={() => onRate(rating)}
              className={`${buttonClass} rounded font-bold transition-all ${
                currentRating === rating
                  ? 'bg-blue-600 text-white shadow-lg scale-110'
                  : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700'
              }`}
            >
              {rating}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render Step 1: Head + Configuration Ratings
  const renderStep1 = () => {
    if (category === 'driver' && driverOptions) {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4 px-4">
            <h3 className="text-lg font-semibold text-gray-900">STEP 1: Rate Head + Configuration Combinations</h3>
            {headConfigRatings.size > 0 && (
              <button
                onClick={handleClearStep}
                className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50"
              >
                Clear All ({headConfigRatings.size})
              </button>
            )}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded sticky top-0 z-10">
            <h3 className="font-bold text-blue-900 mb-1">Step 1: Rate Head + Loft Combinations</h3>
            <p className="text-sm text-blue-800">
              Rate each driver head with its loft options. Each row shows one model with all its loft variations.
            </p>
          </div>

          <div className="space-y-4">
            {/* Each head gets its own row with header - no mixing models */}
            {driverOptions.driver_heads.map((head, headIndex) => (
              <div key={headIndex}>
                <div className="text-sm font-semibold text-gray-700 mb-2 px-1">
                  {head.head_brand} {head.head_model}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {head.loft_options?.map(loft => {
                    const key = getHeadConfigKey(head.head_id, loft);
                    return (
                      <div key={key} className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all">
                        {renderRatingButtons(
                          headConfigRatings.get(key),
                          (rating) => updateHeadConfigRating(head.head_id, loft, rating),
                          `${head.head_brand} ${head.head_model} @ ${loft}° Loft`,
                          'small'
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Custom Head Combo Adder */}
          <CustomComboAdder
            type="head"
            category="driver"
            equipmentOptions={allDriverHeads}
            configOptions={[9.0, 10.5, 12.0]} // Common driver lofts
            customCombos={customHeadCombos}
            maxCombos={MAX_CUSTOM_HEADS}
            onAdd={addCustomHeadCombo}
            onRemove={removeCustomHeadCombo}
          />
        </div>
      );
    }

    // Similar rendering for irons and wedges...
    if (category === 'iron' && ironOptions) {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4 px-4">
            <h3 className="text-lg font-semibold text-gray-900">STEP 1: Rate Head + Configuration Combinations</h3>
            {headConfigRatings.size > 0 && (
              <button
                onClick={handleClearStep}
                className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50"
              >
                Clear All ({headConfigRatings.size})
              </button>
            )}
          </div>

          <div className="bg-green/10 border-l-4 border-green-ink p-4 rounded sticky top-0 z-10">
            <h3 className="font-bold text-green-ink mb-1">Step 1: Rate Head + Set Configuration Combinations</h3>
            <p className="text-sm text-green-ink">
              Rate each iron head with its set configuration options. Each row shows one model with all its set configurations.
            </p>
          </div>

          <div className="space-y-4">
            {/* Each head gets its own row with header - no mixing models */}
            {ironOptions.iron_heads.map((head, headIndex) => (
              <div key={headIndex}>
                <div className="text-sm font-semibold text-gray-700 mb-2 px-1">
                  {head.head_brand} {head.head_model}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {head.set_config_options?.map(setConfig => {
                    const key = getHeadConfigKey(head.head_id, setConfig);
                    return (
                      <div key={key} className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-green-ink hover:shadow-md transition-all">
                        {renderRatingButtons(
                          headConfigRatings.get(key),
                          (rating) => updateHeadConfigRating(head.head_id, setConfig, rating),
                          `${head.head_brand} ${head.head_model} - ${setConfig}`,
                          'small'
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Custom Head Combo Adder */}
          <CustomComboAdder
            type="head"
            category="iron"
            equipmentOptions={allIronHeads}
            configOptions={["4-PW", "5-PW", "6-PW", "5-9", "4-9"]} // Common iron set configs
            customCombos={customHeadCombos}
            maxCombos={MAX_CUSTOM_HEADS}
            onAdd={addCustomHeadCombo}
            onRemove={removeCustomHeadCombo}
          />
        </div>
      );
    }

    if (category === 'wedge' && wedgeOptions) {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4 px-4">
            <h3 className="text-lg font-semibold text-gray-900">STEP 1: Rate Head + Configuration Combinations</h3>
            {headConfigRatings.size > 0 && (
              <button
                onClick={handleClearStep}
                className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50"
              >
                Clear All ({headConfigRatings.size})
              </button>
            )}
          </div>

          <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded sticky top-0 z-10">
            <h3 className="font-bold text-purple-900 mb-1">Step 1: Rate Model + Loft Configuration Combinations</h3>
            <p className="text-sm text-purple-800">
              Rate each wedge model with its loft configuration options. Each row shows one model with all its loft configurations.
            </p>
          </div>

          <div className="space-y-4">
            {/* Each model gets its own row with header - no mixing models */}
            {wedgeOptions.wedge_models.map((model, modelIndex) => (
              <div key={modelIndex}>
                <div className="text-sm font-semibold text-gray-700 mb-2 px-1">
                  {model.model_brand} {model.model_name}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {model.loft_configs?.map(loftConfig => {
                    const key = getHeadConfigKey(model.model_id, loftConfig);
                    return (
                      <div key={key} className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:shadow-md transition-all">
                        {renderRatingButtons(
                          headConfigRatings.get(key),
                          (rating) => updateHeadConfigRating(model.model_id, loftConfig, rating),
                          `${model.model_brand} ${model.model_name} - ${loftConfig}`,
                          'small'
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Custom Head Combo Adder */}
          <CustomComboAdder
            type="head"
            category="wedge"
            equipmentOptions={allWedgeModels}
            configOptions={["50-54-58", "52-56-60", "48-52-56-60", "50-56-60"]} // Common wedge loft configs
            customCombos={customHeadCombos}
            maxCombos={MAX_CUSTOM_HEADS}
            onAdd={addCustomHeadCombo}
            onRemove={removeCustomHeadCombo}
          />
        </div>
      );
    }

    return null;
  };

  // Render Step 2: Shaft + Flex Ratings
  const renderStep2 = () => {
    const headerClasses = category === 'driver'
      ? 'bg-blue-50 border-l-4 border-blue-600 p-4 rounded'
      : category === 'iron'
      ? 'bg-green/10 border-l-4 border-green-ink p-4 rounded'
      : 'bg-purple-50 border-l-4 border-purple-600 p-4 rounded';

    const titleClasses = category === 'driver'
      ? 'font-bold text-blue-900 mb-1'
      : category === 'iron'
      ? 'font-bold text-green-ink mb-1'
      : 'font-bold text-purple-900 mb-1';

    const textClasses = category === 'driver'
      ? 'text-sm text-blue-800'
      : category === 'iron'
      ? 'text-sm text-green-ink'
      : 'text-sm text-purple-800';

    // Flatten all shaft+flex combinations
    const allCombinations: Array<{
      shaftId: number;
      shaftName: string;
      flex: string;
      isRecommended: boolean;
      guidance?: string;
      weight?: number;
      tipStiffness?: string;
      buttStiffness?: string;
      launchScore?: number;
    }> = [];

    // Helper to extract launch score from notes
    const extractLaunchScore = (notes?: string): number | undefined => {
      if (!notes) return undefined;
      const match = notes.match(/Launch Score:\s*(\d+)/);
      return match ? parseInt(match[1]) : undefined;
    };

    // NEW structure: iterate through shaft models and their flex variants
    // Build shaft name with weight to differentiate variants (e.g., "Ventus Blue+ 60g")
    if (category === 'driver' && driverOptions) {
      const seenCombos = new Set<string>();
      driverOptions.driver_heads.forEach(head => {
        head.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}`;
            if (!seenCombos.has(key)) {
              seenCombos.add(key);
              const weight = (flexVariant as any).shaft_weight;
              const shaftName = weight
                ? `${shaftModel.shaft_brand} ${shaftModel.shaft_model} ${weight}g`
                : `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`;
              allCombinations.push({
                shaftId: flexVariant.shaft_id,
                shaftName: shaftName,
                flex: flexVariant.standard_flex,
                isRecommended: flexVariant.is_recommended_flex,
                guidance: undefined,
                weight: weight,
                tipStiffness: (flexVariant as any).shaft_tip_stiffness,
                buttStiffness: (flexVariant as any).shaft_butt_stiffness,
                launchScore: extractLaunchScore((flexVariant as any).shaft_notes)
              });
            }
          });
        });
      });
    } else if (category === 'iron' && ironOptions) {
      const seenCombos = new Set<string>();
      ironOptions.iron_heads.forEach(head => {
        head.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}`;
            if (!seenCombos.has(key)) {
              seenCombos.add(key);
              const weight = (flexVariant as any).shaft_weight;
              const shaftName = weight
                ? `${shaftModel.shaft_brand} ${shaftModel.shaft_model} ${weight}g`
                : `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`;
              allCombinations.push({
                shaftId: flexVariant.shaft_id,
                shaftName: shaftName,
                flex: flexVariant.standard_flex,
                isRecommended: flexVariant.is_recommended_flex,
                guidance: undefined,
                weight: weight,
                tipStiffness: (flexVariant as any).shaft_tip_stiffness,
                buttStiffness: (flexVariant as any).shaft_butt_stiffness,
                launchScore: extractLaunchScore((flexVariant as any).shaft_notes)
              });
            }
          });
        });
      });
    } else if (category === 'wedge' && wedgeOptions) {
      const seenCombos = new Set<string>();
      wedgeOptions.wedge_models.forEach(model => {
        model.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}`;
            if (!seenCombos.has(key)) {
              seenCombos.add(key);
              const weight = (flexVariant as any).shaft_weight;
              const shaftName = weight
                ? `${shaftModel.shaft_brand} ${shaftModel.shaft_model} ${weight}g`
                : `${shaftModel.shaft_brand} ${shaftModel.shaft_model}`;
              allCombinations.push({
                shaftId: flexVariant.shaft_id,
                shaftName: shaftName,
                flex: flexVariant.standard_flex,
                isRecommended: flexVariant.is_recommended_flex,
                guidance: undefined,
                weight: weight,
                tipStiffness: (flexVariant as any).shaft_tip_stiffness,
                buttStiffness: (flexVariant as any).shaft_butt_stiffness,
                launchScore: extractLaunchScore((flexVariant as any).shaft_notes)
              });
            }
          });
        });
      });
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4 px-4">
          <h3 className="text-lg font-semibold text-gray-900">STEP 2: Rate Shaft + Flex Combinations</h3>
          {shaftFlexRatings.size > 0 && (
            <button
              onClick={handleClearStep}
              className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50"
            >
              Clear All ({shaftFlexRatings.size})
            </button>
          )}
        </div>

        <div className={headerClasses}>
          <h3 className={titleClasses}>Step 2: Rate Shaft + Flex Combinations</h3>
          <p className={textClasses}>
            Rate each shaft model with its flex options. Rating 3 at a time for faster comparison!
          </p>
        </div>

        <div className="space-y-4">
          {/* Group by shaft MODEL (ignoring weight/flex) */}
          {(() => {
            // Group combinations by shaft model (brand + model without weight)
            const modelGroups: Record<string, typeof allCombinations> = {};
            allCombinations.forEach(combo => {
              // Extract model name without weight (remove " XXg" or " XX.Xg" suffix)
              // Example: "Fujikura Speeder NX Green 57.5g" → "Fujikura Speeder NX Green"
              const modelName = combo.shaftName.replace(/\s+[\d.]+g$/, '');
              if (!modelGroups[modelName]) {
                modelGroups[modelName] = [];
              }
              modelGroups[modelName].push(combo);
            });

            // Render each model group in its own row
            return Object.entries(modelGroups).map(([modelName, variants], groupIndex) => (
              <div key={groupIndex}>
                <div className="text-sm font-semibold text-gray-700 mb-2 px-1">{modelName}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {variants.map(({ shaftId, shaftName, flex, isRecommended, guidance, weight, tipStiffness, buttStiffness, launchScore }) => {
                    const key = getShaftFlexKey(shaftId, flex);
                    const borderColor = category === 'driver' ? 'border-blue-400' : category === 'iron' ? 'border-green-ink' : 'border-purple-400';
                    return (
                      <div key={key} className={`p-4 bg-white border-2 ${isRecommended ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'} rounded-lg hover:${borderColor} hover:shadow-md transition-all`}>
                        {renderRatingButtons(
                          shaftFlexRatings.get(key),
                          (rating) => updateShaftFlexRating(shaftId, flex, rating),
                          `${shaftName} - ${flex} Flex${isRecommended ? ' ⭐' : ''}`,
                          'small'
                        )}
                        {/* Shaft Details */}
                        <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                          {weight && <div><span className="font-semibold">Weight:</span> {weight}g</div>}
                          {tipStiffness && <div><span className="font-semibold">Tip Stiffness:</span> {tipStiffness}/10</div>}
                          {buttStiffness && <div><span className="font-semibold">Butt Stiffness:</span> {buttStiffness}/10</div>}
                          {launchScore !== undefined && <div><span className="font-semibold">Launch Score:</span> {launchScore}/10</div>}
                        </div>
                        {isRecommended && guidance && (
                          <p className="text-xs text-blue-600 mt-1">{guidance}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Custom Shaft Combo Adder */}
        <CustomComboAdder
          type="shaft"
          category={category}
          equipmentOptions={allShafts}
          flexOptions={['L', 'A', 'R', 'S', 'X']} // Common flex options
          customCombos={customShaftCombos as CustomShaftCombo[]}
          maxCombos={MAX_CUSTOM_SHAFTS}
          onAdd={addCustomShaftCombo}
          onRemove={removeCustomShaftCombo}
        />
      </div>
    );
  };

  // Render Step 2.5: Review Selected Combinations
  const renderStep2_5 = () => {
    // Calculate auto-selected combinations
    const autoSelected = calculateAutoSelectedCombos();

    // Calculate expert-curated combinations
    const expertCurated = calculateExpertCuratedCombos(autoSelected.highHeads, autoSelected.highShafts);

    // Combine all selected combinations
    const allSelected = [...expertCurated, ...autoSelected.combos];

    // Update selectedCombinations state
    if (selectedCombinations.length === 0 || selectedCombinations.length !== allSelected.length) {
      setSelectedCombinations(allSelected);
    }

    const headerClasses = category === 'driver'
      ? 'bg-blue-50 border-l-4 border-blue-600 p-4 rounded'
      : category === 'iron'
      ? 'bg-green/10 border-l-4 border-green-ink p-4 rounded'
      : 'bg-purple-50 border-l-4 border-purple-600 p-4 rounded';

    const titleClasses = category === 'driver'
      ? 'font-bold text-blue-900 mb-1'
      : category === 'iron'
      ? 'font-bold text-green-ink mb-1'
      : 'font-bold text-purple-900 mb-1';

    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-300">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Step 2.5: Review Selected Combinations
          </h3>

          <div className="bg-white p-4 rounded shadow-sm mb-4">
            <h4 className="font-semibold text-gray-800 mb-2">
              ✓ Auto-selected {allSelected.length} combinations:
            </h4>

            <ul className="space-y-1 text-sm text-gray-700">
              {expertCurated.length > 0 && (
                <li>• {expertCurated.length} Expert-Curated Combos ⭐</li>
              )}
              <li>• {autoSelected.highHigh} Top-Rated Combos (high×high)</li>
              <li>• {autoSelected.highLow} Mixed-Rating Combos</li>
              <li>• {autoSelected.lowLow} Low-Rated Combos</li>
              <li>• {autoSelected.random} Random Combos</li>
            </ul>
          </div>

          {expertCurated.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 p-4 rounded mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">
                Your Expert-Curated Combinations:
              </h4>
              <div className="space-y-1">
                {expertCurated.map((combo, idx) => (
                  <div key={idx} className="text-sm text-gray-700">
                    {idx + 1}. {combo.headName} × {combo.shaftName} ({combo.flex}) {combo.badge}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                ⭐⭐ = Both components were your selections<br/>
                ⭐ = One component was your selection
              </p>
            </div>
          )}

          <details className="mb-4">
            <summary className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
              ▼ Show All {allSelected.length} Selected Combos
            </summary>
            <div className="mt-2 max-h-60 overflow-y-auto bg-white p-3 rounded border">
              {allSelected.map((combo, idx) => (
                <div key={idx} className="text-sm text-gray-700 py-1 border-b last:border-0">
                  {combo.headName} × {combo.shaftName} ({combo.flex}) {combo.badge || ''}
                </div>
              ))}
            </div>
          </details>

          <div className="bg-blue-50 border border-blue-300 p-3 rounded">
            <p className="text-sm text-gray-700">
              <strong>Why these combinations?</strong> We've selected a diverse mix to train the recommendation system:
              high-rated combos (likely good matches), mixed ratings (borderline cases for learning),
              and low-rated combos (negative examples). This ensures quality training data without overwhelming you!
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Add more random combinations to Step 3
  const addRandomCombinations = (count: number) => {
    if (selectedCombinations.length + count > MAX_STEP3_TOTAL_COMBOS) {
      toast.error(`Maximum ${MAX_STEP3_TOTAL_COMBOS} combinations allowed`);
      return;
    }

    // Get all possible combinations
    const allCombos = generateAllPossibleCombinations();

    // Filter out already selected
    const available = allCombos.filter(c =>
      !selectedCombinations.some(s =>
        s.headId === c.headId &&
        s.config === c.config &&
        s.shaftId === c.shaftId &&
        s.flex === c.flex
      )
    );

    if (available.length === 0) {
      toast.error('No more combinations available');
      return;
    }

    // Random sample
    const newCombos = randomSample(available, count);
    setSelectedCombinations([...selectedCombinations, ...newCombos]);

    toast.success(`Added ${newCombos.length} random combinations`);
  };

  // Render Step 3: Final Combination Ratings
  const renderStep3 = () => {
    // Use selected combinations instead of all combinations
    const combinations = selectedCombinations.length > 0
      ? selectedCombinations
      : generateAllPossibleCombinations().slice(0, MAX_STEP3_INITIAL_COMBOS);

    // Separate expert-curated from auto-selected
    const expertCombos = combinations.filter(c => c.expertCurated);
    const autoCombos = combinations.filter(c => !c.expertCurated);

    const headerClasses = category === 'driver'
      ? 'bg-blue-50 border-l-4 border-blue-600 p-4 rounded'
      : category === 'iron'
      ? 'bg-green/10 border-l-4 border-green-ink p-4 rounded'
      : 'bg-purple-50 border-l-4 border-purple-600 p-4 rounded';

    const titleClasses = category === 'driver'
      ? 'font-bold text-blue-900 mb-1'
      : category === 'iron'
      ? 'font-bold text-green-ink mb-1'
      : 'font-bold text-purple-900 mb-1';

    const textClasses = category === 'driver'
      ? 'text-sm text-blue-800'
      : category === 'iron'
      ? 'text-sm text-green-ink'
      : 'text-sm text-purple-800';

    const tipClasses = category === 'driver'
      ? 'text-xs text-blue-700 mt-2'
      : category === 'iron'
      ? 'text-xs text-green-ink mt-2'
      : 'text-xs text-purple-700 mt-2';

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4 px-4">
          <h3 className="text-lg font-semibold text-gray-900">STEP 3: Rate Final Combinations</h3>
          {combinationRatings.size > 0 && (
            <button
              onClick={handleClearStep}
              className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50"
            >
              Clear All ({combinationRatings.size})
            </button>
          )}
        </div>

        <div className={headerClasses}>
          <h3 className={titleClasses}>Step 3: Rate Final Combinations</h3>
          <p className={textClasses}>
            Rate the complete combinations of (Head + Configuration) × (Shaft + Flex).
            These ratings represent how well the specific combination works for this golfer.
          </p>
          <p className={tipClasses}>
            <strong>Note:</strong> We've pre-selected {combinations.length} combinations for you to focus on the most relevant matches!
          </p>
        </div>

        {/* Expert-Curated Combinations Section */}
        {expertCombos.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <span className="text-2xl mr-2">⭐</span>
              Expert-Curated Combinations (Rate These First)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              These combinations include equipment you specifically selected as perfect matches!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expertCombos.map((combo) => {
                const key = getCombinationKey(combo.headId, combo.config, combo.shaftId, combo.flex);
                const rating = combinationRatings.get(key);

                return (
                  <div
                    key={key}
                    className="p-4 border-2 border-yellow-400 bg-white rounded-lg shadow-md"
                  >
                    <div className="text-sm font-medium text-gray-800 mb-1">
                      {combo.headName} + {combo.shaftName} ({combo.flex})
                    </div>
                    <div className="text-xs text-blue-600 mb-2 font-medium">
                      {combo.badge} {combo.badge === '⭐⭐' ? 'Both your selections' : 'One your selection'}
                    </div>

                    {renderRatingButtons(
                      rating?.rating,
                      (r) => updateCombinationRating(combo.headId, combo.config, combo.shaftId, combo.flex, r, rating?.notes),
                      'Combined Rating',
                      'small'
                    )}

                    <textarea
                      placeholder="Notes (optional)"
                      value={rating?.notes || ''}
                      onChange={(e) => updateCombinationNotes(combo.headId, combo.config, combo.shaftId, combo.flex, e.target.value)}
                      className="w-full mt-2 px-2 py-1 text-xs border border-gray-300 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                      rows={1}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Auto-Selected Combinations Section */}
        {autoCombos.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">
              Auto-Selected Combinations
            </h3>
            <div className="space-y-4">
              {/* Group by HEAD (driver/iron/wedge model + config) */}
              {(() => {
                // Group combinations by head (headId + config)
                const headGroups: Record<string, typeof autoCombos> = {};
                autoCombos.forEach(combo => {
                  // Use headName as the group key (already includes model + loft/config)
                  const headKey = combo.headName;
                  if (!headGroups[headKey]) {
                    headGroups[headKey] = [];
                  }
                  headGroups[headKey].push(combo);
                });

                // Render each head group with header
                return Object.entries(headGroups).map(([headName, combos], groupIndex) => (
                  <div key={groupIndex}>
                    <div className="text-sm font-semibold text-gray-700 mb-2 px-1">{headName}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {combos.map((combo) => {
                        const key = getCombinationKey(combo.headId, combo.config, combo.shaftId, combo.flex);
                        const rating = combinationRatings.get(key);
                        const borderColor = category === 'driver' ? 'border-blue-400' : category === 'iron' ? 'border-green-ink' : 'border-purple-400';

                        return (
                          <div
                            key={key}
                            className={`p-4 border-2 border-gray-200 bg-white rounded-lg hover:${borderColor} hover:shadow-md transition-all`}
                          >
                            <div className="text-sm font-medium text-gray-800 mb-2">
                              + {combo.shaftName} ({combo.flex})
                            </div>

                            {renderRatingButtons(
                              rating?.rating,
                              (r) => updateCombinationRating(combo.headId, combo.config, combo.shaftId, combo.flex, r, rating?.notes),
                              'Combined Rating',
                              'small'
                            )}

                            <textarea
                              placeholder="Notes (optional)"
                              value={rating?.notes || ''}
                              onChange={(e) => updateCombinationNotes(combo.headId, combo.config, combo.shaftId, combo.flex, e.target.value)}
                              className="w-full mt-2 px-2 py-1 text-xs border border-gray-300 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                              rows={1}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Add More Random Combos Button */}
        {selectedCombinations.length < MAX_STEP3_TOTAL_COMBOS && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mt-6">
            <p className="text-gray-600 mb-3">
              Want to contribute more training data?
            </p>
            <button
              onClick={() => addRandomCombinations(5)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
            >
              + Rate 5 More Random Combos
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Current: {selectedCombinations.length} / Max: {MAX_STEP3_TOTAL_COMBOS}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const getCategoryColor = () => {
    return category === 'driver' ? 'blue' : category === 'iron' ? 'green' : 'purple';
  };

  const getAllHeadConfigCombinations = () => {
    const allCombos: any[] = [];

    if (category === 'driver' && driverOptions) {
      driverOptions.driver_heads.forEach(head => {
        // Loft options are now at HEAD level, not inside flex combinations
        head.loft_options?.forEach(loft => {
          allCombos.push({ headId: head.head_id, loft });
        });
      });
    } else if (category === 'iron' && ironOptions) {
      ironOptions.iron_heads.forEach(head => {
        // Set config options are now at HEAD level
        head.set_config_options?.forEach(set => {
          allCombos.push({ headId: head.head_id, set });
        });
      });
    } else if (category === 'wedge' && wedgeOptions) {
      wedgeOptions.wedge_models.forEach(model => {
        // Loft config options are now at MODEL level
        model.loft_configs?.forEach(config => {
          allCombos.push({ modelId: model.model_id, config });
        });
      });
    }

    return allCombos;
  };

  const getAllShaftFlexCombinations = () => {
    const allCombos: any[] = [];

    if (category === 'driver' && driverOptions) {
      const seenShaftFlex = new Set<string>();
      driverOptions.driver_heads.forEach(head => {
        head.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}-${flexVariant.standard_flex}`;
            if (!seenShaftFlex.has(key)) {
              seenShaftFlex.add(key);
              allCombos.push({ shaftId: flexVariant.shaft_id, flex: flexVariant.standard_flex });
            }
          });
        });
      });
    } else if (category === 'iron' && ironOptions) {
      const seenShaftFlex = new Set<string>();
      ironOptions.iron_heads.forEach(head => {
        head.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}-${flexVariant.standard_flex}`;
            if (!seenShaftFlex.has(key)) {
              seenShaftFlex.add(key);
              allCombos.push({ shaftId: flexVariant.shaft_id, flex: flexVariant.standard_flex });
            }
          });
        });
      });
    } else if (category === 'wedge' && wedgeOptions) {
      const seenShaftFlex = new Set<string>();
      wedgeOptions.wedge_models.forEach(model => {
        model.shaft_models?.forEach(shaftModel => {
          shaftModel.flex_variants?.forEach(flexVariant => {
            const key = `${flexVariant.shaft_id}-${flexVariant.standard_flex}`;
            if (!seenShaftFlex.has(key)) {
              seenShaftFlex.add(key);
              allCombos.push({ shaftId: flexVariant.shaft_id, flex: flexVariant.standard_flex });
            }
          });
        });
      });
    }

    return allCombos;
  };

  const getAllFinalCombinations = () => {
    const headConfigs = getAllHeadConfigCombinations().length;
    const shaftFlexes = getAllShaftFlexCombinations().length;
    return Array.from({ length: headConfigs * shaftFlexes }, (_, i) => i);
  };

  const getStepCompletionPercentage = (step: 1 | 2 | 3): number => {
    if (step === 1) {
      const totalHeadConfigs = getAllHeadConfigCombinations().length;
      return totalHeadConfigs > 0 ? Math.round((headConfigRatings.size / totalHeadConfigs) * 100) : 0;
    }
    if (step === 2) {
      const totalShaftFlex = getAllShaftFlexCombinations().length;
      return totalShaftFlex > 0 ? Math.round((shaftFlexRatings.size / totalShaftFlex) * 100) : 0;
    }
    const totalCombos = getAllFinalCombinations().length;
    return totalCombos > 0 ? Math.round((combinationRatings.size / totalCombos) * 100) : 0;
  };

  const getStepProgress = () => {
    if (currentStep === 1) return headConfigRatings.size;
    if (currentStep === 2) return shaftFlexRatings.size;
    return combinationRatings.size;
  };

  // Silent validation - used for disabled state (no toasts!)
  const canProceedToNextStep = (): boolean => {
    if (currentStep === 1) {
      const totalHeadConfigs = getAllHeadConfigCombinations().length;
      const completionRate = totalHeadConfigs > 0 ? headConfigRatings.size / totalHeadConfigs : 0;
      return headConfigRatings.size > 0 && completionRate >= 0.5;
    }

    if (currentStep === 2) {
      const totalShaftFlex = getAllShaftFlexCombinations().length;
      const completionRate = totalShaftFlex > 0 ? shaftFlexRatings.size / totalShaftFlex : 0;
      return shaftFlexRatings.size > 0 && completionRate >= 0.5;
    }

    if (currentStep === 2.5) {
      // Step 2.5 is just a review step, always allow proceeding
      return true;
    }

    return false;
  };

  // Validation with user feedback - used when user clicks Next Step button
  const validateAndProceedToNextStep = (): boolean => {
    if (currentStep === 1) {
      const totalHeadConfigs = getAllHeadConfigCombinations().length;
      const completionRate = totalHeadConfigs > 0 ? headConfigRatings.size / totalHeadConfigs : 0;

      if (headConfigRatings.size === 0) {
        toast.error('Please rate at least one head configuration');
        return false;
      }

      if (completionRate < 0.5) {
        const required = Math.ceil(totalHeadConfigs * 0.5);
        toast.error(`Please rate at least 50% of head configurations (${required} of ${totalHeadConfigs}). Currently: ${headConfigRatings.size}`);
        return false;
      }
      return true;
    }

    if (currentStep === 2) {
      const totalShaftFlex = getAllShaftFlexCombinations().length;
      const completionRate = totalShaftFlex > 0 ? shaftFlexRatings.size / totalShaftFlex : 0;

      if (shaftFlexRatings.size === 0) {
        toast.error('Please rate at least one shaft/flex combination');
        return false;
      }

      if (completionRate < 0.5) {
        const required = Math.ceil(totalShaftFlex * 0.5);
        toast.error(`Please rate at least 50% of shaft/flex combinations (${required} of ${totalShaftFlex}). Currently: ${shaftFlexRatings.size}`);
        return false;
      }
      return true;
    }

    if (currentStep === 2.5) {
      // Step 2.5 is just a review step, always allow proceeding
      return true;
    }

    return false;
  };

  const headerGradient = category === 'driver'
    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-xl'
    : category === 'iron'
    ? 'bg-green-ink text-white p-4 rounded-t-xl'
    : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-xl';

  // Format profile field values with units
  const formatProfileValue = (field: string, value: any): string => {
    if (value === null || value === undefined) return 'N/A';

    if (field.includes('speed')) return `${value} mph`;
    if (field.includes('angle')) return `${value}°`;
    if (field === 'height_inches') {
      const feet = Math.floor(value / 12);
      const inches = value % 12;
      return `${feet}'${inches}"`;
    }
    if (field.includes('distance')) return `${value} yds`;
    if (field.includes('spin')) return `${value} rpm`;
    if (field.includes('weight')) return `${value} lbs`;
    if (field === 'handicap') return value.toString();
    if (field === 'age') return `${value} yrs`;
    return value.toString();
  };

  // Get friendly field label
  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      handicap: 'Handicap',
      swing_speed_driver: 'Driver Speed',
      swing_speed_7iron: '7-Iron Speed',
      club_speed_driver: 'Club Speed',
      ball_speed: 'Ball Speed',
      age: 'Age',
      height_inches: 'Height',
      weight_lbs: 'Weight',
      launch_angle: 'Launch',
      spin_rate: 'Spin',
      attack_angle: 'Attack',
      club_path: 'Path',
      smash_factor: 'Smash',
      shot_shape: 'Shape',
      ball_flight: 'Flight'
    };
    return labels[field] || field;
  };

  // Render profile context panel
  const renderProfileContext = () => {
    if (selectedProfileFields.length === 0) return null;

    if (profileContextMinimized) {
      return (
        <div className="sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-purple-50 border-b shadow-sm p-2">
          <button
            onClick={() => setProfileContextMinimized(false)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            📊 Show Profile Context ({selectedProfileFields.length} fields)
          </button>
        </div>
      );
    }

    return (
      <div className="sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-purple-50 border-b shadow-md p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold text-gray-700">
            👤 Golfer Profile Context
          </h4>
          <button
            onClick={() => setProfileContextMinimized(true)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Minimize
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {selectedProfileFields.map(field => {
            const value = profile[field];
            if (value === null || value === undefined) return null;

            return (
              <div
                key={field}
                className="bg-white rounded px-2 py-1 border border-gray-200"
              >
                <span className="text-xs text-gray-500">
                  {getFieldLabel(field)}:
                </span>
                <span className="text-xs font-bold text-gray-900 ml-1">
                  {formatProfileValue(field, value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Toaster position="top-right" />
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={headerGradient}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-2xl font-bold">Detailed Equipment Rating (New Grouped System)</h2>
              <p className="text-sm text-blue-100">Profile #{profile.id} - 3-Tier Grouped Rating</p>
              {draftStatus === 'offline' && (
                <div className="mt-2 inline-flex items-center gap-2 bg-amber-500 bg-opacity-30 text-amber-50 px-3 py-1 rounded-full text-xs font-semibold">
                  Offline — progress is held on this device until the connection returns
                </div>
              )}
              {hasLoadedSavedRatings && (
                <div className="mt-2 inline-flex items-center gap-2 bg-green/100 bg-opacity-20 text-green-ink px-3 py-1 rounded-full text-xs font-semibold">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                  </svg>
                  Resumed from saved progress
                  <button
                    onClick={() => { clearDraft(); }}
                    className="ml-1 hover:bg-white hover:bg-opacity-20 rounded px-2 py-0.5 transition-colors"
                    title="Clear saved progress and start fresh"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => { setCategory('driver'); setCurrentStep(1); }}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                category === 'driver'
                  ? 'bg-white text-blue-600'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }`}
            >
              Drivers
            </button>
            <button
              onClick={() => { setCategory('iron'); setCurrentStep(1); }}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                category === 'iron'
                  ? 'bg-white text-green-ink'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }`}
            >
              Irons
            </button>
            <button
              onClick={() => { setCategory('wedge'); setCurrentStep(1); }}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                category === 'wedge'
                  ? 'bg-white text-purple-600'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }`}
            >
              Wedges
            </button>
          </div>

          {/* Step Progress */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {[1, 2, 3].map((step) => (
                <button
                  key={step}
                  onClick={() => setCurrentStep(step as 1 | 2 | 3)}
                  disabled={step > currentStep && !canProceedToNextStep()}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    currentStep === step
                      ? 'bg-white text-blue-600'
                      : step < currentStep
                      ? 'bg-green/100 text-white'
                      : 'bg-white bg-opacity-10 text-white opacity-50 cursor-not-allowed'
                  }`}
                >
                  {step < currentStep ? '✓ ' : ''}
                  Step {step}
                </button>
              ))}
            </div>
            <div className="text-sm text-white bg-white bg-opacity-20 px-3 py-1 rounded-full">
              {currentStep === 1 && `${headConfigRatings.size} rated • ${getStepCompletionPercentage(1)}% complete`}
              {currentStep === 2 && `${shaftFlexRatings.size} rated • ${getStepCompletionPercentage(2)}% complete`}
              {currentStep === 3 && `${combinationRatings.size} rated • ${getStepCompletionPercentage(3)}% complete`}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Profile Context Panel - Sticky */}
          {renderProfileContext()}

          <div className="p-6">
            {loading && (
              <div className="py-6">
                <p className="text-center text-gray-600 mb-4">Loading equipment options...</p>
                <SkeletonLoader count={5} />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {!loading && !error && (
              <>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 2.5 && renderStep2_5()}
                {currentStep === 3 && renderStep3()}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-4 rounded-b-xl flex items-center justify-between">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => {
                  if (currentStep === 2) setCurrentStep(1);
                  else if (currentStep === 2.5) setCurrentStep(2);
                  else if (currentStep === 3) setCurrentStep(2.5);
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                ← Previous Step
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handlePartialSaveAndNext}
              disabled={submitting}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Save current ratings and move to the next profile"
            >
              {submitting ? 'Saving...' : 'Save & Skip to Next'}
            </button>

            {currentStep < 3 ? (
              <button
                onClick={() => {
                  if (validateAndProceedToNextStep()) {
                    if (currentStep === 1) setCurrentStep(2);
                    else if (currentStep === 2) setCurrentStep(2.5);
                    else if (currentStep === 2.5) setCurrentStep(3);
                  }
                }}
                disabled={!canProceedToNextStep()}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next Step →
              </button>
            ) : (
              <button
                onClick={() => handleSubmit()}
                disabled={submitting || combinationRatings.size === 0}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : `Submit ${combinationRatings.size} Ratings`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedRatingModal_v2;
